/**
 * M4S-5663 — a new iOOH crosstab's banner breaks out each DMA by exposure.
 *
 * Dennis Kim's banner plans give every market a column pair split by exposure ("Atlanta, GA -
 * Unexposed", "Atlanta, GA - Exposed"), the way the iOOH base already splits Gender, Age and
 * Income. M4S-5608 put the market table in Rows, and the plan keeps it there; this adds the banner
 * group on top of it, once, when the crosstab is created (`withStartingSetup`, `after-iooh-base`).
 * Spec: docs/pdlc/crosstabs-geography-banner.md.
 *
 * Each group is ONE ordinary nest (M4S-5605). Layer 1 is the market question, and layer 2 is the
 * split the base's own banner shows as Unexposed and Exposed. So compute does not change, the
 * columns count exactly what the base's Unexposed and Exposed columns count, and the researcher can
 * open the group in the Nest tab to add a market, leave one out or rename a column.
 *
 * What gets built:
 *  - **Every market with completes**, in the order the survey lists them, with no business limit.
 *    DMAs are chosen on purpose, and a programmed market with no completes is a fielding problem
 *    fixed before tabs are run (Dennis Kim, 2026-10-06), so in practice these are the programmed
 *    markets. Counting completes keeps an unexpected empty market, or a long programmed list
 *    fielded in a few markets (a brand-lift DMA carries up to 132), from adding empty columns.
 *  - **DMA only, never State.** A study programs every state rather than the ones it targets, so
 *    Dennis Kim's team builds a State breakout with Nest when a plan asks for one (#ai-iooh-crosstabs,
 *    2026-10-05). State's table stays in Rows (`geography.ts`).
 *  - **No group above 100 markets**, the most one Nest holds (`NEST_MAX_COLUMNS`, two columns
 *    each). The researcher is told, and can nest the markets they need. No real study comes close.
 *  - **No group for one market.** Its two columns would repeat the base's Unexposed and Exposed.
 *  - **No group when one side never answered.** A market question asked only in the Control wave
 *    leaves every Exposed column empty. The researcher is told.
 *  - **Names follow the banner plan**: `<market> - Unexposed`, then `<market> - Exposed`, the market
 *    as the survey spells it, under a "DMA" header.
 *
 * Pure. The respondents are counted by the caller's `countColumns`, which is the compute the table
 * runs, so a market is kept or dropped on the same numbers the researcher will see.
 */

import type {
    CustomVariable,
    NestLayer,
    NestMember,
    NestRecipe,
    VariableBucket,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

import { isGeographyQuestion } from './geography';
import {
    NEST_ID_PREFIX,
    NEST_LABEL_SEPARATOR,
    NEST_MAX_COLUMNS,
    SCALE_NET_ID,
    memberLabel,
    nestedVariable,
} from './nest-variables';
import type { CrosstabQuestionDto } from './question-formatter';
import { sortQuestionsBySurveyOrder } from './survey-order';

/**
 * The most markets a new crosstab breaks out by exposure: as many as one Nest holds, two columns
 * each. Not a business limit (until 2026-10-06 it was 12): a bigger group would compute, but its
 * saved recipe would fail `parseNest`, so the Nest tab could not reopen it.
 */
export const GEOGRAPHY_BANNER_MAX_MARKETS = NEST_MAX_COLUMNS / 2;

/**
 * The groups' id prefix. They are nests (`cv_nest_`), so the Nest tab opens them, and never
 * `cv_bl_`, so the iOOH base's next apply keeps them and a removed group stays removed.
 */
export const GEOGRAPHY_BANNER_ID_PREFIX = `${NEST_ID_PREFIX}geo_`;

/**
 * The market questions that get a banner group: DMA only. State is a market question for Rows
 * (`GEOGRAPHY_QIDS`) but never gets a group, because a study programs every state.
 */
const BANNER_MARKET_QIDS = ['DMA'] as const;

type MarketQid = (typeof BANNER_MARKET_QIDS)[number];

/** Each market question's group header, and what a note to the researcher calls its answers. */
const GROUPS: Record<MarketQid, { header: string; plural: string }> = {
    DMA: { header: 'DMA', plural: 'markets' },
};

const isBannerMarket = (column: string): column is MarketQid =>
    (BANNER_MARKET_QIDS as readonly string[]).includes(column);

/** The base's exposure words, as the banner plan writes them. */
const UNEXPOSED = 'Unexposed';
const EXPOSED = 'Exposed';

/**
 * Respondents per banner column, counted the way the table counts them. `banner` lists question
 * ids or the ids of `variables`; the answer maps each column id (`<QID>:<answer id>` for a question,
 * `<variable id>:<label>` for a variable) to its base. Null when the study's answers are not loaded.
 */
export type CountColumns = (
    banner: string[],
    variables: CustomVariable[],
) => Record<string, number> | null;

export interface GeographyBannerInput {
    /** The project's questions. */
    catalog: readonly CrosstabQuestionDto[];
    /** The layout as the iOOH base left it: its banner shows the exposure split. */
    activeBanner: readonly string[];
    customVariables: readonly CustomVariable[];
    /** The session's stamp, so a second call names the same ids. */
    stamp: string;
    countColumns: CountColumns;
}

export interface GeographyBanner {
    /** The groups to append to the banner, in survey order. */
    variables: CustomVariable[];
    /** One sentence for the researcher per market question that got no group and should have. */
    notes: string[];
}

/** The id a market question's group gets in this session. */
export function geographyBannerId(qid: MarketQid, stamp: string): string {
    return `${GEOGRAPHY_BANNER_ID_PREFIX}${qid.toLowerCase()}_${stamp}`;
}

/** The answer id a catalog option is known by: the rail's and the Nest tab's rule. */
const answerId = (o: CrosstabQuestionDto['options'][number], i: number): string =>
    o.option_id[0] || o.option || `opt_${i}`;

const baseColumn = (q: CrosstabQuestionDto) => (q.responseColumnId ?? q.qid).toUpperCase();

/** One condition: `column` equals `id`. The members a Nest tab answer layer makes are this. */
function answerBucket(label: string, column: string, id: string): VariableBucket {
    return { label, join: 'any', conditions: [{ questionId: column, operator: 'eq', value: id }] };
}

function copyBucket(b: VariableBucket): VariableBucket {
    return {
        label: b.label,
        join: b.join,
        conditions: b.conditions.map((c) => ({
            questionId: c.questionId,
            operator: c.operator,
            value: c.value,
            ...(c.nextJoin ? { nextJoin: c.nextJoin } : {}),
        })),
    };
}

// ── The exposure layer ───────────────────────────────────────────────────────

export interface ExposureLayer {
    source: string;
    label: string;
    unexposed: NestMember;
    exposed: NestMember;
}

/**
 * The base's Unexposed or Exposed variable as a member of the "Cell / Ad Recall" group, keyed as
 * the Nest tab keys a variable's bucket label. Null unless all of its buckets share one label.
 */
function variableMember(cv: CustomVariable | undefined, label: string): NestMember | null {
    if (!cv || cv.kind !== 'logic' || cv.buckets.length === 0) return null;
    const own = cv.buckets[0].label;
    if (!own.trim() || cv.buckets.some((b) => b.label !== own)) return null;
    return { key: `${cv.id}#${own}`, label, buckets: cv.buckets.map(copyBucket) };
}

/**
 * The split the base's banner shows as Unexposed and Exposed, as the second layer of a nest.
 *
 *  - **The base's own variables**, `cv_bl_unexposed` and `cv_bl_exposed`, which it builds when the
 *    survey has no combined Cell / Ad Recall question. Taken as their group, the way the Nest tab
 *    offers them, so the columns count exactly what those two columns count.
 *  - **The combined question** (CELLADRECALLCV on 281 of the 313 recent brand-lift surveys), which
 *    the base puts in the banner as it is. Its answers "Unexposed" and "Exposed" are the base's
 *    columns of those names.
 *
 * Null when the banner shows neither, and then no market group can be built.
 */
export function exposureLayer(
    catalog: readonly CrosstabQuestionDto[],
    activeBanner: readonly string[],
    customVariables: readonly CustomVariable[],
): ExposureLayer | null {
    const byId = new Map(customVariables.map((v) => [v.id, v] as const));
    const unexposedCv = byId.get('cv_bl_unexposed');
    const exposedCv = byId.get('cv_bl_exposed');
    const group = unexposedCv?.cvGroupName?.trim();
    if (group && exposedCv?.cvGroupName?.trim() === group) {
        const unexposed = variableMember(unexposedCv, UNEXPOSED);
        const exposed = variableMember(exposedCv, EXPOSED);
        if (unexposed && exposed)
            return { source: `group:${group}`, label: group, unexposed, exposed };
    }

    const questions = new Map(catalog.map((q) => [q.qid.toUpperCase(), q] as const));
    for (const id of activeBanner) {
        if (byId.has(id)) continue;
        const q = questions.get(id.toUpperCase());
        if (!q) continue;
        const find = (word: string) =>
            q.options.findIndex((o) => o.option.trim().toLowerCase() === word.toLowerCase());
        const u = find(UNEXPOSED);
        const e = find(EXPOSED);
        if (u === -1 || e === -1) continue;
        const column = baseColumn(q);
        const member = (word: string, at: number): NestMember => {
            const value = answerId(q.options[at], at);
            return {
                key: `${column}=${value}`,
                label: word,
                buckets: [answerBucket(word, column, value)],
            };
        };
        return {
            source: column,
            label: q.question.trim() || q.qid,
            unexposed: member(UNEXPOSED, u),
            exposed: member(EXPOSED, e),
        };
    }
    return null;
}

// ── The market layer ─────────────────────────────────────────────────────────

interface MarketQuestion {
    qid: MarketQid;
    /** The question text, for the Nest tab's layer heading. */
    label: string;
    /** The catalog questions that read this column: the question and its wave variants. */
    qids: string[];
    /** Every answer, in the order the survey lists them; a variant's new answers follow. */
    answers: { id: string; label: string; from: string }[];
}

/**
 * The study's market questions, in survey order, each with every answer it lists. A wave that
 * words the question differently is a variant (`DMA__v2`) reading the same column, so its answers
 * join the question's, and every member reads the column itself (`DMA`), which is every wave's.
 */
function marketQuestions(catalog: readonly CrosstabQuestionDto[]): MarketQuestion[] {
    const out = new Map<MarketQid, MarketQuestion>();
    for (const q of sortQuestionsBySurveyOrder(catalog)) {
        if (!isGeographyQuestion(q)) continue;
        const column = baseColumn(q);
        if (!isBannerMarket(column)) continue;
        let market = out.get(column);
        if (!market) {
            market = { qid: column, label: q.question.trim() || column, qids: [], answers: [] };
            out.set(column, market);
        }
        market.qids.push(q.qid);
        const seen = new Set(market.answers.map((a) => a.id.toUpperCase()));
        q.options.forEach((o, i) => {
            const id = answerId(o, i);
            if (SCALE_NET_ID.test(id) || seen.has(id.toUpperCase())) return;
            seen.add(id.toUpperCase());
            market.answers.push({ id, label: memberLabel(o.option), from: q.qid });
        });
    }
    return [...out.values()].filter((m) => m.answers.length > 0);
}

/**
 * The answers as layer-1 members, named as the survey names them. Two answers that read the same
 * would make one column (compute merges same-label buckets), so each of those is told apart by its
 * answer id.
 */
function marketMembers(qid: MarketQid, answers: MarketQuestion['answers']): NestMember[] {
    const count = new Map<string, number>();
    for (const a of answers) {
        const k = a.label.trim().toLowerCase();
        count.set(k, (count.get(k) ?? 0) + 1);
    }
    return answers.map((a) => {
        const label =
            (count.get(a.label.trim().toLowerCase()) ?? 0) > 1 ? `${a.label} (${a.id})` : a.label;
        return { key: `${qid}=${a.id}`, label, buckets: [answerBucket(label, qid, a.id)] };
    });
}

function groupVariable(
    market: MarketQuestion,
    members: NestMember[],
    exposure: ExposureLayer,
    stamp: string,
): CustomVariable {
    const layers: NestLayer[] = [
        { source: market.qid, label: market.label, members },
        {
            source: exposure.source,
            label: exposure.label,
            members: [exposure.unexposed, exposure.exposed],
        },
    ];
    const recipe: NestRecipe = { version: 1, layers };
    return nestedVariable({
        id: geographyBannerId(market.qid, stamp),
        name: GROUPS[market.qid].header,
        recipe,
        placement: 'banner',
    });
}

/** A column's label: the market's, then the exposure word, as the nest names its columns. */
const columnLabel = (market: NestMember, word: NestMember) =>
    `${market.label}${NEST_LABEL_SEPARATOR}${word.label}`;

// ── The groups ───────────────────────────────────────────────────────────────

/**
 * The banner groups a new iOOH crosstab gets, one per market question that has from 2 to
 * `GEOGRAPHY_BANNER_MAX_MARKETS` markets with completes on both sides of the exposure split, and a note for each question that
 * was skipped for a reason the researcher cannot see. Never throws for a study it cannot read: no
 * market question, no exposure split in the banner or no answers loaded all give no group.
 */
export function geographyBanner(input: GeographyBannerInput): GeographyBanner {
    const none: GeographyBanner = { variables: [], notes: [] };
    const have = new Set(input.customVariables.map((v) => v.id));
    const markets = marketQuestions(input.catalog).filter(
        (m) => !have.has(geographyBannerId(m.qid, input.stamp)),
    );
    if (markets.length === 0) return none;
    const exposure = exposureLayer(input.catalog, input.activeBanner, input.customVariables);
    if (!exposure) return none;

    // 1. Which markets have completes. Each catalog question that reads the column is counted as
    //    the table counts a banner question: one column per answer, `<QID>:<answer id>`.
    const tallies = input.countColumns(
        markets.flatMap((m) => m.qids),
        [],
    );
    if (!tallies) return none;
    const notes: string[] = [];
    const candidates: { market: MarketQuestion; members: NestMember[] }[] = [];
    for (const market of markets) {
        const answered = market.answers.filter(
            (a) => (tallies[`${a.from.toUpperCase()}:${a.id}`] ?? 0) > 0,
        );
        if (answered.length < 2) continue;
        const { header, plural } = GROUPS[market.qid];
        if (answered.length > GEOGRAPHY_BANNER_MAX_MARKETS) {
            notes.push(
                `${header} isn't in the banner: ${answered.length} ${plural} have completes, more than one banner group holds (${GEOGRAPHY_BANNER_MAX_MARKETS}). To break out the ones you need, nest ${header} with the exposure split in Create Variable → Nest.`,
            );
            continue;
        }
        candidates.push({ market, members: marketMembers(market.qid, answered) });
    }
    if (candidates.length === 0) return { variables: [], notes };

    // 2. Each market by exposure, counted as the columns the group would show.
    const drafts = candidates.map((c) => groupVariable(c.market, c.members, exposure, input.stamp));
    const counts = input.countColumns(
        drafts.map((v) => v.id),
        drafts,
    );
    if (!counts) return { variables: [], notes };

    const variables: CustomVariable[] = [];
    candidates.forEach(({ market, members }, i) => {
        const n = (m: NestMember, word: NestMember) =>
            counts[`${drafts[i].id}:${columnLabel(m, word)}`] ?? 0;
        // A market whose respondents are in neither exposure group has nothing to show.
        const kept = members.filter((m) => n(m, exposure.unexposed) + n(m, exposure.exposed) > 0);
        if (kept.length < 2) return;
        const { header } = GROUPS[market.qid];
        for (const word of [exposure.unexposed, exposure.exposed]) {
            if (kept.every((m) => n(m, word) === 0)) {
                notes.push(
                    `${header} isn't in the banner: none of the ${word.label} respondents answered it, so it can't be broken out by exposure.`,
                );
                return;
            }
        }
        variables.push(
            kept.length === members.length
                ? drafts[i]
                : groupVariable(market, kept, exposure, input.stamp),
        );
    });
    return { variables, notes };
}
